import { useState, useEffect, useRef, useCallback } from "react";
import { useAppContext } from "../context/useAppContext";

const GRAVITY = 0.5;
const DAMPING = 0.6;
const FRICTION = 0.92;
const BOX_HEIGHT = 48;
const BOX_PADDING_H = 20;

export default function GravityBox({ BOXES = [] }) {
    const containerRef = useRef(null);
    const boxRefs = useRef({});
    const boxSizes = useRef({});
    const stateRef = useRef({});
    const animRef = useRef(null);
    const draggingRef = useRef(null);
    const hasDroppedRef = useRef(false);

    const { darkTheme } = useAppContext();

    const [positions, setPositions] = useState({});
    const [visible, setVisible] = useState(false);

    // ✅ Measure box sizes
    const measureBoxes = useCallback(() => {
        const sizes = {};
        BOXES.forEach((_, index) => {
            const el = boxRefs.current[index];
            if (el) {
                sizes[index] = {
                    w: el.offsetWidth,
                    h: el.offsetHeight
                };
            }
        });
        boxSizes.current = sizes;
    }, [BOXES]);

    // ✅ Random X positions
    const getRandomPositions = (W) => {
        const positions = [];
        const sizes = boxSizes.current;

        for (let i = 0; i < BOXES.length; i++) {
            const bw = sizes[i]?.w ?? 100;

            let x, valid, tries = 0;

            do {
                valid = true;
                x = Math.random() * Math.max(0, W - bw);

                for (let j = 0; j < i; j++) {
                    const pw = sizes[j]?.w ?? 100;

                    if (Math.abs(positions[j] - x) < Math.max(bw, pw) + 12) {
                        valid = false;
                        break;
                    }
                }

                tries++;
            } while (!valid && tries < 100);

            positions.push(x);
        }

        return positions;
    };

    // ✅ Trigger on scroll
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasDroppedRef.current) {
                    hasDroppedRef.current = true;
                    measureBoxes();
                    setVisible(true);
                }
            },
            { threshold: 0.3 }
        );

        observer.observe(el);
        return () => observer.disconnect();
    }, [measureBoxes]);

    // ✅ Initialize boxes
    useEffect(() => {
        if (!visible) return;

        requestAnimationFrame(() => {
            measureBoxes();

            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return;

            const W = rect.width;
            const randomX = getRandomPositions(W);

            const init = {};

            BOXES.forEach((_, i) => {
                init[i] = {
                    x: randomX[i],
                    y: -(BOX_HEIGHT + i * 60),
                    vx: (Math.random() - 0.5) * 1.2,
                    vy: 0,
                    angle: Math.random() * 30 - 15,
                    dragging: false
                };
            });

            stateRef.current = init;

            setPositions(
                Object.fromEntries(
                    Object.entries(init).map(([k, v]) => [
                        k,
                        { x: v.x, y: v.y, angle: v.angle }
                    ])
                )
            );
        });
    }, [visible]);

    // ✅ Collision between boxes
    const resolveCollisions = () => {
        const state = stateRef.current;
        const ids = Object.keys(state);
        const sizes = boxSizes.current;

        for (let i = 0; i < ids.length; i++) {
            for (let j = i + 1; j < ids.length; j++) {
                const a = state[ids[i]];
                const b = state[ids[j]];

                const aw = sizes[ids[i]]?.w ?? 100;
                const bw = sizes[ids[j]]?.w ?? 100;

                const ah = BOX_HEIGHT;
                const bh = BOX_HEIGHT;

                if (
                    a.x < b.x + bw &&
                    a.x + aw > b.x &&
                    a.y < b.y + bh &&
                    a.y + ah > b.y
                ) {
                    const overlapX =
                        Math.min(a.x + aw, b.x + bw) - Math.max(a.x, b.x);

                    const overlapY =
                        Math.min(a.y + ah, b.y + bh) - Math.max(a.y, b.y);

                    if (overlapX < overlapY) {
                        const push = overlapX / 2;

                        if (a.x < b.x) {
                            if (!a.dragging) a.x -= push;
                            if (!b.dragging) b.x += push;
                        } else {
                            if (!a.dragging) a.x += push;
                            if (!b.dragging) b.x -= push;
                        }

                        a.vx *= 0.7;
                        b.vx *= 0.7;
                    } else {
                        const push = overlapY / 2;

                        if (a.y < b.y) {
                            if (!a.dragging) a.y -= push;
                            if (!b.dragging) b.y += push;
                        } else {
                            if (!a.dragging) a.y += push;
                            if (!b.dragging) b.y -= push;
                        }

                        a.vy *= 0.7;
                        b.vy *= 0.7;
                    }
                }
            }
        }
    };

    // ✅ Physics loop
    useEffect(() => {
        if (!visible) return;

        const loop = () => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) {
                animRef.current = requestAnimationFrame(loop);
                return;
            }

            const containerW = rect.width;
            const containerH = rect.height;

            const state = stateRef.current;

            Object.keys(state).forEach((id) => {
                const s = state[id];
                if (s.dragging) return;

                const bw = boxSizes.current[id]?.w ?? 100;
                const bh = BOX_HEIGHT;

                const maxX = containerW - bw;
                const maxY = containerH - bh;

                // gravity
                s.vy += GRAVITY;

                s.x += s.vx;
                s.y += s.vy;

                // walls
                if (s.y >= maxY) {
                    s.y = maxY;
                    s.vy *= -DAMPING;
                    s.vx *= FRICTION;
                    if (Math.abs(s.vy) < 0.5) s.vy = 0;
                }

                if (s.y <= 0) {
                    s.y = 0;
                    s.vy = 0;
                }

                if (s.x <= 0) {
                    s.x = 0;
                    s.vx *= -DAMPING;
                }

                if (s.x >= maxX) {
                    s.x = maxX;
                    s.vx *= -DAMPING;
                }
            });

            // 🔥 resolve collisions
            resolveCollisions();

            // 🔥 IMPORTANT FIX: clamp AGAIN
            Object.keys(state).forEach((id) => {
                const s = state[id];

                const bw = boxSizes.current[id]?.w ?? 100;
                const bh = BOX_HEIGHT;

                const maxX = containerW - bw;
                const maxY = containerH - bh;

                if (s.x < 0) s.x = 0;
                if (s.x > maxX) s.x = maxX;
                if (s.y < 0) s.y = 0;
                if (s.y > maxY) s.y = maxY;
            });

            setPositions(
                Object.fromEntries(
                    Object.keys(state).map((id) => [
                        id,
                        {
                            x: state[id].x,
                            y: state[id].y,
                            angle: state[id].angle
                        }
                    ])
                )
            );

            animRef.current = requestAnimationFrame(loop);
        };

        animRef.current = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(animRef.current);
    }, [visible]);

    // ✅ Drag start
    const startDrag = (clientX, clientY, id) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const s = stateRef.current[id];

        draggingRef.current = {
            id,
            offsetX: clientX - rect.left - s.x,
            offsetY: clientY - rect.top - s.y,
            lastX: clientX
        };

        s.dragging = true;
    };

    // ✅ Drag move
    useEffect(() => {
        const onMove = (clientX, clientY) => {
            const d = draggingRef.current;
            if (!d) return;

            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return;

            const s = stateRef.current[d.id];

            const bw = boxSizes.current[d.id]?.w ?? 100;
            const bh = BOX_HEIGHT;

            const maxX = rect.width - bw;
            const maxY = rect.height - bh;

            const dx = clientX - d.lastX;
            d.lastX = clientX;

            s.x = Math.max(0, Math.min(maxX, clientX - rect.left - d.offsetX));
            s.y = Math.max(0, Math.min(maxY, clientY - rect.top - d.offsetY));

            s.angle += dx * 0.5;
        };

        const onEnd = () => {
            const d = draggingRef.current;
            if (!d) return;

            stateRef.current[d.id].dragging = false;
            draggingRef.current = null;
        };

        const mm = (e) => onMove(e.clientX, e.clientY);

        window.addEventListener("mousemove", mm);
        window.addEventListener("mouseup", onEnd);

        return () => {
            window.removeEventListener("mousemove", mm);
            window.removeEventListener("mouseup", onEnd);
        };
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                position: "relative",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                userSelect: "none"
            }}
        >
            {BOXES.map((box, index) => {
                const pos = positions[index];
                const isDragging = draggingRef.current?.id === index;

                return (
                    <div
                        key={index}
                        ref={(el) => {
                            if (el) boxRefs.current[index] = el;
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            startDrag(e.clientX, e.clientY, index);
                        }}
                        style={{
                            position: "absolute",
                            left: pos ? pos.x : -9999,
                            top: pos ? pos.y : -9999,
                            width: "max-content",
                            height: BOX_HEIGHT,
                            padding: `0 ${BOX_PADDING_H}px`,
                            background: darkTheme
                                ? "rgba(71, 170, 167)"
                                : "rgb(0, 54, 61)",
                            borderRadius: "50px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: isDragging ? "grabbing" : "grab",
                            fontSize: 18,
                            fontWeight: 900,
                            color: "#fff",
                            boxShadow: isDragging
                                ? "0 18px 40px rgba(0,0,0,0.3)"
                                : "0 6px 18px rgba(0,0,0,0.2)",
                            transform: `rotate(${pos?.angle || 0}deg) scale(${
                                isDragging ? 1.1 : 1
                            })`,
                            transition: "transform 0.1s",
                            zIndex: isDragging ? 10 : 1,
                            whiteSpace: "nowrap",
                            visibility: pos ? "visible" : "hidden"
                        }}
                    >
                        {box}
                    </div>
                );
            })}
        </div>
    );
}